import { useCallback, useContext, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/modal';
import { PlusIcon } from 'lucide-react';
import { useNotification } from '@/components/notifications';
import { DialogClose } from '@/components/ui/dialog';
import { LoadingSpinner } from '@/components/loading-spinner';
import { createCollection } from '@/services/data-sources/create-collection';
import { updateCollection } from '@/services/data-sources/update-collection';
import { Collection, CollectionSource } from '@/types/source-types';
import { CollectionForm, validateCollectionName } from './collection-form';
import {
  CollectionTypeOption,
  CollectionTypeSelector,
} from './collection-type-selector';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { CollectionSourceSection } from './collection-source-section';
import type { WordPressSiteFormErrors } from './wordpress-site-form';
import type { WordPressSiteFormState } from './wordpress-site-form';
import {
  DEFAULT_WORDPRESS_SCHEDULE,
  createEmptySiteState,
  validateWordPressSites,
  buildSitesPayload,
} from './wordpress-source-utils';
import { AuthContext } from '@/context/auth-context';

interface NewCollectionModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
  collections: Collection[];
  setCollections: (collections: Collection[]) => void;
  navigateToCollection: (collection: string) => void;
}

export const NewCollectionModal = ({
  isModalOpen = false,
  setIsModalOpen,
  collections,
  setCollections,
  navigateToCollection,
}: NewCollectionModalProps) => {
  const notification = useNotification();
  const { isAdmin } = useContext(AuthContext);
  const {
    isUnstructuredCollectionsEnabled,
    isStructuredCollectionsEnabled,
    isCollectionSourceEnabled,
  } = useLaunchDarkly();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState('');
  const [collectionType, setCollectionType] =
    useState<CollectionTypeOption>('unstructured');
  const [collectionSource, setCollectionSource] =
    useState<CollectionSource>('uploads');
  const [wordpressSites, setWordpressSites] = useState<
    WordPressSiteFormState[]
  >(() => {
    const initial = createEmptySiteState();
    return [initial];
  });
  const [siteErrors, setSiteErrors] = useState<
    Record<string, WordPressSiteFormErrors>
  >({});
  const [expandedSiteId, setExpandedSiteId] = useState<string | null>(
    () => wordpressSites[0]?.siteId ?? null,
  );
  const [reconcileEnabled, setReconcileEnabled] = useState(false);

  const resetWordPressForm = useCallback(() => {
    setCollectionSource('uploads');
    const emptySite = createEmptySiteState();
    setWordpressSites([emptySite]);
    setExpandedSiteId(emptySite.siteId);
    setSiteErrors({});
    setReconcileEnabled(false);
  }, []);

  useEffect(() => {
    if (
      !isUnstructuredCollectionsEnabled &&
      isStructuredCollectionsEnabled &&
      collectionType === 'unstructured'
    ) {
      setCollectionType('structured');
    }
  }, [
    isUnstructuredCollectionsEnabled,
    isStructuredCollectionsEnabled,
    collectionType,
  ]);

  useEffect(() => {
    if (collectionType !== 'unstructured') {
      resetWordPressForm();
    }
  }, [collectionType, resetWordPressForm]);

  const handleCreateCollection = async () => {
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();

    const validationError = validateCollectionName(name);
    if (validationError) {
      setHasError(validationError);
      return;
    }

    const isStructured = collectionType === 'structured';
    const isWordPress = !isStructured && collectionSource === 'wordpress_api';

    let sourceConfig = null;

    if (isWordPress) {
      const errors = validateWordPressSites(wordpressSites);

      setSiteErrors(errors);

      if (Object.keys(errors).length > 0) {
        // Auto-expand the first errored site
        const firstErrorSiteId = Object.keys(errors)[0];
        setExpandedSiteId(firstErrorSiteId);
        return;
      }

      sourceConfig = {
        ...buildSitesPayload(wordpressSites),
        reconcile_enabled: reconcileEnabled,
      };
    } else {
      setSiteErrors({});
    }

    setIsLoading(true);

    try {
      // Create the collection
      const createResponse = await createCollection({
        collectionName: trimmedName,
        collectionType,
        source: isWordPress ? 'wordpress_api' : 'uploads',
        sourceConfig,
      });

      if (createResponse?.detail) {
        setHasError(createResponse.detail);
        return;
      }

      const newCollectionId = createResponse.data?.id;

      if (!newCollectionId) {
        setHasError('Failed to get collection ID');
        return;
      }

      // If description is provided, update the collection with description
      if (trimmedDescription) {
        const updateResponse = await updateCollection({
          id: newCollectionId,
          name: trimmedName,
          description: trimmedDescription,
        });

        if (updateResponse?.detail) {
          // Collection was created but description update failed
          notification.warning(
            'Collection created but failed to set description',
          );
        }
      }

      // Update the collections list with the new collection
      setCollections([
        ...collections,
        createResponse?.data as unknown as Collection,
      ]);

      notification.success('Collection created successfully');

      // Reset form and close modal
      setName('');
      setDescription('');
      setHasError('');
      setCollectionType('unstructured');
      resetWordPressForm();
      setIsModalOpen(false);

      // Navigate to the new collection
      navigateToCollection(newCollectionId);
    } catch (error) {
      console.error('Create collection error:', error);
      notification.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const Title = () => (
    <div className='flex items-center gap-2'>
      <PlusIcon className='h-5 w-5' />
      <span id='data-sources-add-collection-title'>Create collection</span>
    </div>
  );

  const Footer = () => (
    <>
      <DialogClose asChild>
        <Button id='data-sources-add-collection-cancel' variant='outline'>
          Cancel
        </Button>
      </DialogClose>
      <Button
        id='data-sources-add-collection-save'
        disabled={!name?.trim() || isLoading}
        onClick={handleCreateCollection}
      >
        {isLoading ? (
          <>
            <LoadingSpinner /> Creating...
          </>
        ) : (
          'Create collection'
        )}
      </Button>
    </>
  );

  return (
    <Modal
      isDialogOpen={isModalOpen}
      setIsDialogOpen={(isModalOpen) => {
        setName('');
        setDescription('');
        setHasError('');
        setCollectionType('unstructured');
        resetWordPressForm();
        setIsModalOpen(isModalOpen);
      }}
      title={<Title />}
      footer={<Footer />}
    >
      <div className='space-y-6'>
        <CollectionForm
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
          hasError={hasError}
          setHasError={setHasError}
          isLoading={isLoading}
          onSubmit={handleCreateCollection}
          nameLabel='Collection name'
        />
        {isStructuredCollectionsEnabled && (
          <CollectionTypeSelector
            value={collectionType}
            onChange={setCollectionType}
            disabled={isLoading}
            showUnstructured={isUnstructuredCollectionsEnabled}
          />
        )}
        {collectionType === 'unstructured' &&
          (isCollectionSourceEnabled || isAdmin) && (
            <CollectionSourceSection
              source={collectionSource}
              onSourceChange={(next) => {
                setCollectionSource(next);
                if (next === 'wordpress_api') {
                  setWordpressSites((prev) =>
                    prev.map((site) => ({
                      ...site,
                      scheduleCron:
                        site.scheduleCron?.trim() || DEFAULT_WORDPRESS_SCHEDULE,
                    })),
                  );
                } else {
                  setSiteErrors({});
                }
              }}
              wordpressSites={wordpressSites}
              onWordPressSitesChange={(sites) => {
                setWordpressSites(sites);
                setSiteErrors({});
              }}
              siteErrors={siteErrors}
              reconcileEnabled={reconcileEnabled}
              onReconcileEnabledChange={setReconcileEnabled}
              disabled={isLoading}
              expandedSiteId={expandedSiteId}
              onExpandedSiteIdChange={setExpandedSiteId}
            />
          )}
      </div>
    </Modal>
  );
};
